"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { MUTE_STORAGE_KEY, resolveSoundUrl } from "./sounds";
import type { WidgetSounds } from "./widget-config";
import { useWidgetInstance } from "./widget-instance-context";

/**
 * Plays notification sounds for inbound + outbound messages.
 *
 * Audio elements are created once on mount + reused across plays
 * (avoids re-`new Audio(url)` on every event). They use `preload="none"`,
 * so the file downloads lazily on first play(), not on page load.
 * `playInbound` / `playOutbound` are stable refs — pass them straight
 * to event listeners without `useCallback` gymnastics.
 *
 * **Visitor mute wins.** Visitors who tap the mute toggle persist the
 * preference in `localStorage["wexio:sound-muted"]`. Operator-level
 * `sounds.enabled` is the *default* (audible) but mute always overrides.
 *
 * Browser autoplay policy: most browsers block audio until the user
 * has interacted with the page. The first `play()` call inside a
 * click handler unlocks subsequent silent triggers (e.g. SSE inbound
 * fires after the visitor sent their first message → already
 * unlocked). We swallow `NotAllowedError` instead of crashing.
 */
export function useSoundPlayer(sounds: WidgetSounds | null) {
  const storage = useWidgetInstance().storage;
  const [muted, setMuted] = useState(false);
  const inboundRef = useRef<HTMLAudioElement | null>(null);
  const outboundRef = useRef<HTMLAudioElement | null>(null);

  // Read persisted mute preference on first mount. Without this the
  // toggle would reset between page reloads — bad UX.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const stored = storage.getItem(MUTE_STORAGE_KEY);
    setMuted(stored === "1");
  }, [storage]);

  // (Re)preload audio elements whenever the sound config changes.
  useEffect(() => {
    if (typeof window === "undefined") return;
    inboundRef.current?.pause();
    outboundRef.current?.pause();

    const inboundUrl = resolveSoundUrl(sounds?.inboundSoundId);
    const outboundUrl = resolveSoundUrl(sounds?.outboundSoundId);
    const volume =
      typeof sounds?.volume === "number" ? Math.max(0, Math.min(1, sounds.volume)) : 0.5;

    inboundRef.current = inboundUrl ? buildAudio(inboundUrl, volume) : null;
    outboundRef.current = outboundUrl ? buildAudio(outboundUrl, volume) : null;

    return () => {
      inboundRef.current?.pause();
      outboundRef.current?.pause();
    };
  }, [sounds]);

  const toggleMuted = useCallback(() => {
    setMuted((prev) => {
      const next = !prev;
      try {
        storage.setItem(MUTE_STORAGE_KEY, next ? "1" : "0");
      } catch {
        // Privacy mode / quota errors — accept the runtime toggle
        // and skip persistence rather than crashing.
      }
      return next;
    });
  }, [storage]);

  const play = useCallback(
    (kind: "inbound" | "outbound") => {
      if (muted) return;
      if (sounds && sounds.enabled === false) return;
      const ref = kind === "inbound" ? inboundRef.current : outboundRef.current;
      if (!ref) return;
      ref.currentTime = 0;
      ref.play().catch(() => {
        // Autoplay blocked or asset failed — swallow. Next user-
        // gesture-triggered call unlocks the policy.
      });
    },
    [muted, sounds],
  );

  return {
    muted,
    toggleMuted,
    playInbound: () => play("inbound"),
    playOutbound: () => play("outbound"),
  };
}

function buildAudio(src: string, volume: number): HTMLAudioElement {
  // preload="none": do NOT download the (uncompressed WAV) file on mount.
  // These sounds were eagerly fetching ~3.6MB on every page load, even for
  // visitors who never open the chat. The file now loads lazily on the first
  // play() call (which is user-gesture triggered, so the tiny first-play
  // latency is unnoticeable). Set preload BEFORE src so no eager fetch starts.
  const audio = new Audio();
  audio.preload = "none";
  audio.volume = volume;
  audio.src = src;
  return audio;
}
